import { useState, type FormEvent } from "react";
import { newsletterFeatures } from "@/mocks/homeData";

const FORM_URL = "https://readdy.ai/api/form/daenqj3ep053ijub1epg";

type Status = { kind: "idle" | "sending" | "success" | "error"; msg?: string };

export default function Subscribe() {
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = String(data.get("website_alt") || "").trim();

    // 泛化反馈：命中蜜罐时不真正提交，但与正常提交返回同样的成功提示
    const genericSuccess = () => setStatus({ kind: "success", msg: "订阅成功！最新瓜报会第一时间送到你的邮箱。" });

    if (honeypot) {
      genericSuccess();
      return;
    }

    const email = String(data.get("email") || "");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus({ kind: "error", msg: "请输入有效的邮箱地址，我们才好把瓜报送到你手上。" });
      return;
    }

    setStatus({ kind: "sending" });
    const payload = new URLSearchParams();
    // 保留 honeypot 为空时不发送；其余字段正常组装
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string") payload.append(key, value);
    });

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let code: string | undefined;
      let serverMsg: string | undefined;
      try {
        const parsed = responseText ? JSON.parse(responseText) : {};
        code = parsed?.code;
        serverMsg = parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? undefined;
      } catch {
        code = undefined;
      }
      const lowerMsg = (serverMsg || "").toLowerCase();
      const failed =
        !res.ok ||
        (code !== undefined && code !== "OK") ||
        lowerMsg.includes("spam") ||
        responseText.toLowerCase().includes("form data is spam");

      if (!failed && res.ok) {
        form.reset();
        genericSuccess();
      } else {
        setStatus({ kind: "error", msg: serverMsg || "提交失败，请稍后重试，或换个邮箱试试。" });
      }
    } catch {
      setStatus({ kind: "error", msg: "网络似乎开小差了，请检查后重试。" });
    }
  }

  return (
    <section id="join" className="relative overflow-hidden bg-background-50 py-16 md:py-24">
      <div className="absolute inset-0 -z-0 bg-gradient-to-br from-primary-100/70 via-background-50 to-accent-100/70" aria-hidden="true"></div>
      <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-xs font-medium text-accent-900">
              <i className="ri-mail-send-line" aria-hidden="true"></i>免费订阅 · 随时退订
            </span>
            <h2 className="mt-5 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              每天 08:00，把今天最值得吃的瓜<b className="text-primary-600">讲给你听</b>
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
              不想每天自己刷几十个热搜？把这事交给我们。订阅后，每天早晨你会收到一封干净、清楚的头号大瓜解读。
            </p>
            <ul className="mt-6 space-y-3">
              {newsletterFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-foreground-800">
                  <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-check-line text-xs" aria-hidden="true"></i>
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-background-200/70 bg-background-50/90 p-6 backdrop-blur-sm md:p-8">
            <h3 className="font-heading text-xl font-bold text-foreground-900">订阅「每日瓜报」</h3>
            <form
              data-readdy-form="今日大瓜 资讯订阅"
              onSubmit={handleSubmit}
              noValidate
              className="trap-protection mt-5"
            >
              {/* 反垃圾蜜罐字段 */}
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

              <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="请输入你的邮箱"
                  autoComplete="email"
                  className="w-full flex-1 rounded-full border border-background-300 bg-background-100 px-5 py-3.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
                <button
                  type="submit"
                  disabled={status.kind === "sending"}
                  className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status.kind === "sending" ? (
                    <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>提交中…</span>
                  ) : (
                    <span className="flex items-center gap-2">立即订阅<i className="ri-send-plane-line" aria-hidden="true"></i></span>
                  )}
                </button>
              </div>

              {status.kind === "success" && (
                <p role="status" className="mt-4 flex items-start gap-2 rounded-xl bg-accent-100 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line mt-0.5" aria-hidden="true"></i>
                  {status.msg}
                </p>
              )}
              {status.kind === "error" && (
                <p role="alert" className="mt-4 flex items-start gap-2 rounded-xl bg-primary-100 px-4 py-3 text-sm text-primary-900">
                  <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
                  {status.msg}
                </p>
              )}
            </form>
            <p className="mt-4 text-xs leading-5 text-foreground-500">
              订阅即代表你同意接收「今日大瓜」邮件。我们只发有用的内容，也绝不转卖你的邮箱信息。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}